import React, { useContext, useEffect, useState } from "react";
import { Button, Flex, Heading, Modal, X } from "@cisco/spaces-dash-lib-styleguide";
import { logout } from "@cisco/spaces-dash-lib-login";
import { Toast } from "@cisco/spaces-dash-lib-notification";
import { getEndUserLicenseAgreement, updatePartnerLicenseCall } from "../../../services/partner-login-service";
import { I18nContext } from "../../../contexts/i18nContext";

interface LicenseAgreementModalProps {
  isOpen: boolean;
  closeModal: () => void;
  showAdminViewSelection: () => void;
}

export function LicenseAgreementModal(props: LicenseAgreementModalProps): JSX.Element {
  const { isOpen, closeModal, showAdminViewSelection } = props;
  const i18n = useContext(I18nContext);

  if (!i18n) {
    throw new Error("I18nContext is not available");
  }

  const { t } = i18n;
  const [licenseAgreement, setLicenseAgreement] = useState("");

  const cancelAgreement = () => {
    sessionStorage.clear();
    logout();
    closeModal();
  };

  const updatePartnerLicense = async () => {
    const tenantId = parseInt(sessionStorage.getItem("tenantId") ?? "0") || 0;
    const userName = sessionStorage.getItem("userName") ?? "";

    const acceptedBy = {
      acceptedBy: userName,
      tenantId,
      version: "v1",
      region: "us" as const
    };

    updatePartnerLicenseCall(acceptedBy).then(
      (data) => {
        if (data.status) {
          showAdminViewSelection();
          closeModal();
        } else {
          Toast.show(data.message || "", "", { status: "negative" });
          closeModal();
        }
      },
      (error: any) => {
        console.log("ERROR : ", error);
        Toast.show(t("auth.login.login-partner-base.error-occurred-contact-support", { ns: "spaces-dash-app-partners-login" }), "", { status: "negative" });
        closeModal();
      }
    );
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await getEndUserLicenseAgreement();
        if (res.status && res.data && res.data.license) {
          setLicenseAgreement(res.data.license.replace(/\n/g, "<br />"));
        } else {
          setLicenseAgreement(t("auth.login.login-partner-base.license-agreement-unavailable-msg", { ns: "spaces-dash-app-partners-login" }));
          Toast.show(t("auth.login.login-partner-base.license-agreement-unavailable-msg", { ns: "spaces-dash-app-partners-login" }), "", { status: "negative" });
          closeModal();
        }
      } catch (error) {
        Toast.show(t("auth.login.login-partner-base.license-agreement-unavailable-msg", { ns: "spaces-dash-app-partners-login" }), "", { status: "negative" });
        console.error(error);
        closeModal();
      }
    })();
  }, []);

  return (
    <Modal isOpen={isOpen} size="xl">
      <Flex justify="space-between" style={{ marginBottom: "16px" }}>
        <Heading>{t("auth.login.login-partner-base.license-agreement", { ns: "spaces-dash-app-partners-login" })}</Heading>
        <X size={16} weight="bold" onClick={closeModal} style={{ cursor: "pointer" }} />
      </Flex>
      <Modal.Content style={{ paddingTop: "16px", paddingBottom: "16px" }}>
        <div dangerouslySetInnerHTML={{ __html: licenseAgreement }} />
      </Modal.Content>
      <Button.Group>
        <Flex style={{ paddingTop: "16px", width: "100%" }} direction="horizontal" justify="flex-end">
          <Button kind="secondary" onClick={cancelAgreement}>
            {t("auth.change-password.cancel-text", { ns: "spaces-dash-app-partners-login" })}
          </Button>
          <Button disabled={licenseAgreement === ""} onClick={updatePartnerLicense}>
            {t("auth.login.login-partner-base.accept-license-agreement", { ns: "spaces-dash-app-partners-login" })}
          </Button>
        </Flex>
      </Button.Group>
    </Modal>
  );
}
